import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const { addItem } = useCart();

  const category = params.get("category") || "all";
  const featured = params.get("featured") === "1";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products/categories");
        setCategories(data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = {};
        if (category !== "all") q.category = category;
        if (featured) q.featured = true;
        if (search) q.search = search;
        const { data } = await api.get("/products", { params: q });
        setProducts(data);
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, featured, search]);

  function setCategory(c) {
    const next = new URLSearchParams(params);
    if (c === "all") next.delete("category"); else next.set("category", c);
    setParams(next);
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-10">
        <div className="text-[11px] tracking-[0.25em] uppercase text-[#4A148C] font-body">
          {featured ? "Curated festive picks" : "Browse the collection"}
        </div>
        <h1 className="font-display text-5xl text-[#2C1810] mt-2">
          {featured ? "Festive Picks" : "Mona\u2019s Creations"}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        <aside className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#4A148C] font-body flex items-center gap-2 mb-3">
              <Search className="w-3.5 h-3.5" /> Search
            </div>
            <Input
              data-testid="shop-search-input"
              placeholder="Search handicrafts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-full border-[#FFD1A6] bg-white/80"
            />
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-[#4A148C] font-body flex items-center gap-2 mb-3">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Categories
            </div>
            <div className="space-y-1">
              <button
                data-testid="cat-all"
                onClick={() => setCategory("all")}
                className={`w-full text-left px-4 py-2 rounded-full font-body text-sm transition-colors ${
                  category === "all" ? "bg-[#E65C00] text-white" : "text-[#2C1810] hover:bg-[#FFE9CE]"
                }`}
              >
                All Handicrafts
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`cat-${c}`}
                  onClick={() => setCategory(c)}
                  className={`w-full text-left px-4 py-2 rounded-full font-body text-sm transition-colors ${
                    category === c ? "bg-[#E65C00] text-white" : "text-[#2C1810] hover:bg-[#FFE9CE]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-6 font-body text-sm text-[#5B3D2A]">
            <div data-testid="shop-count">{loading ? "Loading…" : `${products.length} products`}</div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-white/60 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 bg-white/60 rounded-3xl border border-dashed border-[#FFD1A6]">
              <div className="font-display text-2xl text-[#2C1810]">No products yet</div>
              <p className="font-body text-[#5B3D2A] mt-2">Try a different filter or check back soon.</p>
              <Button
                data-testid="shop-clear-filters"
                onClick={() => { setParams({}); setSearch(""); }}
                className="mt-6 rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white"
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onAdd={(prod) => { addItem(prod, 1); toast.success(`${prod.name} added to cart`); }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
